import React, { useContext } from "react"
import ReactMarkdown from "react-markdown"
import { useStaticQuery, graphql } from "gatsby"
import cx from "classnames"
import styled from "styled-components"
import { useInView } from "react-intersection-observer"
import { theme } from "twin.macro"
import { StrapiRichTextRenderer } from "../strapi/StrapiRichTextRenderer"
import { WaterBackground } from "../components/WaterBackground"
import { useIntl } from "../util/useIntl"
import { PageContext } from "../contexts/PageContext"
import { Hero, HeroAction } from "../components/Hero"

const HeroContainer = styled.div.attrs({
  className: "relative w-full",
})`
  @media (min-width: ${theme("screens.lg")}) {
    margin-top: calc(var(--navbar-height) * -2);
  }
`

const HeroBackground = styled.div.attrs({
  className: "fixed top-0 left-0 w-full h-screen",
})`
  z-index: -1;
`

const bottomInset = 150
const baseHeight = `calc(100vh - ${bottomInset}px)`
const aboveWater = `50vh`
const belowWater = `calc(50vh - ${bottomInset}px)`

const StyledHero = styled(Hero)`
  @media (min-width: ${theme("screens.lg")}) {
    height: ${baseHeight};
  }
`

const Description = styled.div`
  @media (min-width: ${theme("screens.lg")}) {
    height: 100%;
  }
  flex: 1;
  flex-direction: column;
  padding-top: 1rem;
  padding-right: 1rem;
`

const Media = styled.div`
  @media (max-width: ${theme("screens.lg")}) {
    display: flex;
    height: calc(50vh - var(--navbar-height));
    align-items: center;
    justify-content: center;
  }
  @media (min-width: ${theme("screens.lg")}) {
    margin-top: ${belowWater};
  }
`

const Title = styled.div.attrs({
  className:
    "text-3xl sm:text-5xl whitespace-pre-wrap flex flex-col lg:justify-end",
})`
  @media (min-width: ${theme("screens.lg")}) {
    height: ${aboveWater};
  }
`
const Details = styled.div`
  @media (min-width: ${theme("screens.lg")}) {
    height: ${belowWater};
  }
`

export const HeroActionRenderer = ({ data }) => {
  const { intl } = useIntl()
  return (
    <>
      <HeroAction type={data.type} href={intl(data.link?.href).get()}>
        <ReactMarkdown>{data.content.text}</ReactMarkdown>
      </HeroAction>
    </>
  )
}

const heroMarkdownRenderers = {
  paragraph: ({ children }) => {
    return <h1 className="leading-relaxed py-s" children={children} />
  },
}

const HeroVideo = () => {
  const pageContext = useContext(PageContext)
  const staticData = useStaticQuery(graphql`
    query HeroVideoQuery {
      strapi {
        heroVideo {
          config
        }
      }
    }
  `)

  if (!staticData?.strapi?.heroVideo?.config) {
    throw new Error("No hero video config present in api.")
  }

  const config = staticData?.strapi?.heroVideo?.config
  const { language } = pageContext

  return (
    <iframe
      title="hero video"
      src={config.sources[language]}
      {...config.attributes}
      className={cx("rounded-lg", config.attributes.class)}
    ></iframe>
  )
}

export function HeroSectionRenderer({ data }) {
  const { ref: markerRef, inView: markerInView } = useInView({
    initialInView: true,
  })

  const heroActions = data.actions.map(data => {
    return <HeroActionRenderer data={data} key={data.id} />
  })

  return (
    <HeroContainer>
      <HeroBackground>
        <WaterBackground
          color="#67b2d1"
          className={cx(
            "absolute",
            "w-full",
            "left-0",
            "bottom-0",
            "z-0",
            "transform",
            "transition-transform",
            !markerInView && "translate-y-full",
            "duration-500",
            "ease-in-out",
            "h-1/2"
          )}
        />
      </HeroBackground>
      <StyledHero className="relative z-10">
        <Description>
          <Title>
            <StrapiRichTextRenderer
              renderers={heroMarkdownRenderers}
              data={data.heading}
            />
          </Title>
          <Details style={{ height: belowWater }}>
            <StrapiRichTextRenderer data={data.description} />
            <div className="my-m flex flex-col items-start space-y-s md:space-y-0 md:flex-row md:space-x-m">
              {heroActions}
            </div>
          </Details>
        </Description>
        <Media ref={markerRef}>
          <HeroVideo />
        </Media>
      </StyledHero>
    </HeroContainer>
  )
}
